import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import type { User, Organization } from './types'
import { authApi, orgApi, setScopedOrgId } from './api'
import { translate } from './i18n'

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<string | null>
  logout: () => void
  effectiveCountries: string[] | null
  scopedOrgId: string | null
  scopedOrg: Organization | null
  orgs: Organization[]
  setScopedOrg: (orgId: string | null) => void
  isSuperAdmin: boolean
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  login: async () => null,
  logout: () => {},
  effectiveCountries: null,
  scopedOrgId: null,
  scopedOrg: null,
  orgs: [],
  setScopedOrg: () => {},
  isSuperAdmin: false,
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [scopedOrgId, _setScopedOrgId] = useState<string | null>(null)

  const isSuperAdmin = user?.role === 'SUPERADMIN'

  useEffect(() => {
    const stored = localStorage.getItem('icc_user')
    const token = localStorage.getItem('icc_token')
    if (stored && token) {
      try {
        setUser(JSON.parse(stored))
        authApi.me().then(fresh => {
          setUser(fresh)
          localStorage.setItem('icc_user', JSON.stringify(fresh))
        }).catch(() => {})
      } catch {
        localStorage.removeItem('icc_user')
        localStorage.removeItem('icc_token')
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (isSuperAdmin) {
      orgApi.findAll().then(setOrgs).catch(() => {})
    }
  }, [isSuperAdmin])

  const setScopedOrg = useCallback((orgId: string | null) => {
    _setScopedOrgId(orgId)
    setScopedOrgId(orgId)
  }, [])

  const scopedOrg = useMemo(() => {
    if (!scopedOrgId) return null
    return orgs.find(o => o.id === scopedOrgId) ?? null
  }, [scopedOrgId, orgs])

  const effectiveCountries = useMemo(() => {
    if (!user) return null
    if (isSuperAdmin) {
      return scopedOrg ? scopedOrg.countries : null
    }
    const orgCountries: string[] = user.orgCountries ?? []
    const allowed: string[] = user.allowedCountries ?? []
    if (allowed.length === 0) return orgCountries.length > 0 ? orgCountries : null
    return orgCountries.filter(c => allowed.includes(c))
  }, [user, isSuperAdmin, scopedOrg])

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    try {
      const res = await authApi.login(email, password)
      localStorage.setItem('icc_token', res.accessToken)
      localStorage.setItem('icc_user', JSON.stringify(res.user))
      setUser(res.user)
      return null
    } catch (err: any) {
      return err.message?.includes('401')
        ? translate('login.badCredentials')
        : (err.message ?? translate('login.failed'))
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('icc_token')
    localStorage.removeItem('icc_user')
    setUser(null)
    setScopedOrg(null)
  }, [setScopedOrg])

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      effectiveCountries,
      scopedOrgId, scopedOrg, orgs,
      setScopedOrg, isSuperAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
